import { useEffect, useState } from 'react';

interface Options {
  /** Minimum scroll delta (px) before a direction change is committed.
   *  Prevents flicker from sub-pixel/inertia scrolling. */
  threshold?: number;
  /** While scrollY is below this, always return false (header stays visible).
   *  Lets the hero feel anchored before the auto-hide kicks in. */
  topOffset?: number;
  /** If > 0, hide the header after this many ms of no scroll activity.
   *  Only applies while `enableIdle` is true. Set to 0 to disable. */
  idleMs?: number;
  /** Gate for the idle-hide timer. The consumer typically wires this to
   *  "is the user past the first section?" so the hero stays uncovered. */
  enableIdle?: boolean;
}

/**
 * Tracks scroll direction and returns true when the consumer should
 * hide itself. The result becomes true on scroll-down past `topOffset`,
 * and (optionally) after `idleMs` of no scroll activity while
 * `enableIdle` is on. It becomes false on scroll-up or near the top.
 *
 * Reads `window.scrollY` inside `requestAnimationFrame` so the listener
 * stays passive and runs at most once per frame.
 */
export function useHideOnScroll({
  threshold = 6,
  topOffset = 80,
  idleMs = 0,
  enableIdle = false,
}: Options = {}): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const armIdle = () => {
      if (idleTimer !== null) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
      if (!enableIdle || idleMs <= 0) return;
      // Don't idle-hide while still in the topOffset zone — there's nothing
      // useful to hide for, and the topOffset rule would just flip it back.
      if (window.scrollY <= topOffset) return;
      idleTimer = setTimeout(() => setHidden(true), idleMs);
    };

    const update = () => {
      const y = window.scrollY;
      const diff = y - lastY;

      if (y <= topOffset) {
        setHidden(false);
      } else if (Math.abs(diff) >= threshold) {
        setHidden(diff > 0);
      }

      lastY = y;
      ticking = false;
      armIdle();
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    armIdle();

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (idleTimer !== null) clearTimeout(idleTimer);
    };
  }, [threshold, topOffset, idleMs, enableIdle]);

  return hidden;
}
